import { motion, AnimatePresence } from "framer-motion"
import { useTranslation } from "react-i18next"

import { useMemo, useEffect, useRef } from 'react'

import useAuthStore from "../../store/useAuthStore";
import useLayoutStore from "../../store/useLayoutStore";
import useAppStore from "../../store/useAppStore";

const ProfileModal = ({ onClose }) => {
    const { t } = useTranslation("profile")
    const logout = useAuthStore.getState().logout
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const userId = useAuthStore(state => state.userId)
    const userData = useAppStore(state => state.employeesData?.[userId])
    const modalContainerRef = useRef(null)

    const modalData = useMemo(() => {
        if (!userData) {
            return {
                imgUrl: null,
                firstName: '',
                lastName: '',
                email: '',
                presenceKey: 'offline',
                actionKey: 'checkIn',
                buttonIcon: './../sidebar/darkTheme.svg',
            }
        }

        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        const imgUrl = userData.image ? `${baseImgUrl}/employees/${userData.image}` : null

        let presenceKey = 'offline'
        let actionKey = 'checkIn'
        let buttonIcon = './../sidebar/darkTheme.svg'

        if (userData.aktivan_status === 'ulaz') {
            actionKey = 'checkOut'
            if (userData.presenceType === 'remote') {
                presenceKey = 'remote'
                buttonIcon = './../sidebar/Office.svg'
            } else {
                presenceKey = 'office'
                buttonIcon = './../sidebar/Ekran.svg'
            }
        }

        return {
            imgUrl,
            firstName: userData.ime,
            lastName: userData.prezime,
            email: userData.username,
            presenceKey,
            actionKey,
            buttonIcon,
        }
    }, [userData])

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return;
            }

            const profileButton = e.target.closest('.profile-button');
            if (profileButton) {
                return;
            }

            onClose();
        };

        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [onClose]);

    return (
        <>
            <motion.div
                ref={modalContainerRef}
                className={`profile-modal-container ${isDarkTheme ? "dark-profile-modal" : "white-profile-modal"}`}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -14 }}
                transition={{ duration: 0.19, ease: "easeInOut" }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className={`profile-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                    onClick={onClose}
                    aria-label={t("closeAria")}
                >
                    <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ display: "block", margin: "auto" }}
                    >
                        <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                        <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                    </svg>
                </button>
                <LanguageToggleButton />
                <ToggleThemeButton />
                <div className="profile-modal-content">
                    <div className="pmc-name-container">
                        {modalData.firstName} {modalData.lastName}
                    </div>
                    <div className="pmc-email-container">
                        {modalData.email}
                    </div>
                    <div className="pmc-presence-type-container">
                        <img src={modalData.buttonIcon} alt="" />
                        {t(modalData.presenceKey)}
                    </div>
                    <button className="pmc-button pmc-logout-button" onClick={logout}>
                        <img src="./login/Back.svg" alt="" />
                        <span>{t("logout")}</span>
                    </button>
                    <button
                        className="pmc-button pmc-presence-button"
                        onClick={() => {
                            onClose();
                            useAppStore.getState().setIsWorkPresenceFormOpen(true);
                        }}
                    >
                        <img src="./login/ExitDoor.svg" alt="" />
                        <span>{t(modalData.actionKey)}</span>
                    </button>
                </div>
                <div className="profile-modal-img-container">
                    <img
                        key={modalData.imgUrl || "profile-fallback"}
                        src={modalData.imgUrl || "/views/login.svg"}
                        alt=""
                        crossOrigin="use-credentials"
                        className={isDarkTheme ? "dark-profile-modal-img" : "white-profile-modal-img"}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/views/login.svg";
                        }}
                    />
                </div>

            </motion.div>
            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'none' }}
            >

            </motion.div>
        </>
    )
}

export default ProfileModal

const LanguageToggleButton = () => {
    const { t } = useTranslation("profile")
    const language = useLayoutStore((state) => state.language) || "sr"
    const setLanguage = useLayoutStore((state) => state.setLanguage)
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme)
    const nextLanguage = language === "sr" ? "en" : "sr"
    const flagSrc = language === "en" ? "/login/flag-en.svg" : "/login/flag-sr.svg"
    const label = language === "en" ? "EN" : "SR"

    return (
        <button
            type="button"
            className={`profile-language-button ${isDarkTheme ? "profile-language-button-dark" : "profile-language-button-light"}`}
            onClick={(e) => {
                e.stopPropagation()
                setLanguage(nextLanguage)
            }}
            aria-label={t("changeLanguageAria")}
        >
            <AnimatePresence mode="wait" initial={false}>
                <motion.span
                    key={language}
                    className="profile-language-button-inner"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                >
                    <img src={flagSrc} alt="" className="profile-language-flag" />
                    <span>{label}</span>
                </motion.span>
            </AnimatePresence>
        </button>
    )
}

const ToggleThemeButton = () => {
    const { t } = useTranslation("profile")
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const toggleTheme = useLayoutStore(state => state.toggleTheme)

    return (
        <button
            className={`theme-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
            onClick={(e) => {
                e.stopPropagation();
                toggleTheme();
            }}
            aria-label={t("toggleThemeAria")}
        >
            <AnimatePresence mode="wait">
                <motion.img
                    key={isDarkTheme ? "light" : "dark"}
                    src={isDarkTheme ? "./sidebar/lightTheme.svg" : "./sidebar/darkTheme.svg"}
                    alt=""
                    initial={{ scale: 0 }}
                    animate={{ scale: isDarkTheme ? 1 : 0.9 }}
                    exit={{ scale: 0 }}
                    transition={{ duration: 0.19, ease: "easeInOut" }}
                />
            </AnimatePresence>
        </button>
    )
}
